import { VpButton, VpFormControl, VpFormError, VpFormLabel, VpInput } from '@vtmn-play/react'
import Modal from 'components/common/mygame/modal/modal'
import { useTranslation } from 'react-i18next'
import { useContext, useState } from 'react'
import { useApiPostTargetProductivity } from '../../../../core/api/home/productivity/useApiPostTargetProductivity'
import { HomeContext, type StoreTeamSwitchAggregation } from '../../state/home-context'
import { HOMEPAGE_EVENTS } from 'core/constants/homepage-analytics'
import { useHomepageAnalytics } from '../../analytics/use-homepage-analytics'

const getFieldDescriptionLabel = (aggregation: StoreTeamSwitchAggregation): string => {
  return aggregation === 'STORE'
    ? 'general.homepage.target-productivity.edition.instore-label-store'
    : 'general.homepage.target-productivity.edition.instore-label-team'
}

const isValid = (value: string) => {
  return new RegExp(String.raw`^\d+(\.\d)?$`).test(value)
}

interface TargetProductivityEditionProps {
  targetProductivity?: number
}

const TargetProductivityEdition = ({ targetProductivity = null }: TargetProductivityEditionProps) => {
  const { t } = useTranslation()
  const trackHomepageEvent = useHomepageAnalytics()
  const [isOpen, setIsOpen] = useState(false)
  const [target, setTarget] = useState<string>(undefined)
  const [validationError, setValidationError] = useState<boolean>(undefined)
  const { aggregation, channel, storeId, teamId } = useContext(HomeContext)

  const { mutate } = useApiPostTargetProductivity(
    { storeId, teamId, channel, aggregation, targetProductivity: Number.parseFloat(target) || null },
    channel === 'INSTORE' && target !== undefined
  )

  const confirm = () => {
    if (target) {
      mutate()
      trackHomepageEvent(HOMEPAGE_EVENTS.TARGET_PRODUCTIVITY_UPDATED, {
        target_productivity: Number.parseFloat(target)
      })
      setIsOpen(false)
      setTarget(undefined)
    }
  }

  const handleChange = (value: string) => {
    setTarget(value)
    setValidationError(!isValid(value.trim()))
  }

  const open = () => {
    trackHomepageEvent(HOMEPAGE_EVENTS.CLICK_TARGET_PRODUCTIVITY_EDIT)
    setTarget(targetProductivity === null ? undefined : String(targetProductivity))
    setIsOpen(true)
  }

  const close = () => {
    setIsOpen(false)
    setTarget(undefined)
    setValidationError(false)
  }

  const footer = (
    <>
      <VpButton variant="secondary" variantStyle="regular" size="medium" onClick={close}>
        {t('general.homepage.target-productivity.edition.cancel')}
      </VpButton>
      <VpButton
        variant="primary"
        variantStyle="regular"
        size="medium"
        disabled={!target || validationError}
        onClick={confirm}
      >
        {t('general.homepage.target-productivity.edition.confirm')}
      </VpButton>
    </>
  )

  return (
    <div>
      <VpButton variant="secondary" variantStyle="regular" size="small" onClick={open}>
        {t('general.homepage.target-productivity.open')}
      </VpButton>
      <Modal
        open={isOpen}
        onClose={close}
        closeOnClickOutside
        showCloseButton
        title={t('general.homepage.target-productivity.edition.title')}
        footer={footer}
      >
        <p className="mg-target-productivity-edit-content-subtitle vp-body-m">
          {t('general.homepage.target-productivity.edition.instructions')}
        </p>
        <VpFormControl>
          <VpFormLabel>{t(getFieldDescriptionLabel(aggregation))}</VpFormLabel>
          <VpInput
            name="target-productivity"
            value={target ?? ''}
            onChange={event => {
              handleChange(event.target.value)
            }}
          />
          {validationError && (
            <VpFormError>{t('general.homepage.target-productivity.edition.validation-error')}</VpFormError>
          )}
        </VpFormControl>
      </Modal>
    </div>
  )
}
export default TargetProductivityEdition
